import { FieldValues } from 'react-hook-form'
import { useAnalytics } from '../components/Analytics'
import { InputProps } from '../types'
import {
  EVENT_ACTION_BLURRED,
  EVENT_ACTION_CHANGED,
  EVENT_ACTION_FOCUSED,
  EVENT_INPUT_INTERACTION,
  EVENT_PROPERTY_ACTION,
  EVENT_PROPERTY_DESCRIPTOR,
  EVENT_PROPERTY_UI_CONTEXT,
} from '../../utils/analytics/events'
import { useEffect, useRef, useState } from 'react'

export const useInputProps = <TFormValues extends FieldValues>(
  props: InputProps<TFormValues>
) => {
  const initialRender = useRef(true)
  const { analytics } = useAnalytics()
  const { tracking, onFocus, onBlur, onChange, ...restInputProps } = props
  const [value, setValue] = useState('')

  // When the value is changed, start a timer to send the event to amplitude. If the value is changed again before the timer is up, cancel the timer and start a new one. This
  // prevents sending an event for every keystroke.
  useEffect(() => {
    let timeout
    // Without this, we track an onChange event on initial render
    if (initialRender.current) {
      initialRender.current = false
    } else {
      timeout = setTimeout(() => {
        analytics?.track(EVENT_INPUT_INTERACTION, {
          [EVENT_PROPERTY_ACTION]: EVENT_ACTION_CHANGED,
          [EVENT_PROPERTY_DESCRIPTOR]: tracking?.descriptor,
          [EVENT_PROPERTY_UI_CONTEXT]: tracking?.ui_context,
          content_length: value?.toString().length,
        })
      }, 1000)
    }

    return () => clearTimeout(timeout)
  }, [value, analytics, tracking?.ui_context, tracking?.descriptor])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Tracking happens in the above useEffect when the value changes
    setValue(e.target.value)

    if (onChange) {
      onChange(e)
    }
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    analytics?.track(EVENT_INPUT_INTERACTION, {
      [EVENT_PROPERTY_ACTION]: EVENT_ACTION_FOCUSED,
      [EVENT_PROPERTY_DESCRIPTOR]: tracking?.descriptor,
      [EVENT_PROPERTY_UI_CONTEXT]: tracking?.ui_context,
    })

    if (onFocus) {
      onFocus(e)
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    analytics?.track(EVENT_INPUT_INTERACTION, {
      [EVENT_PROPERTY_ACTION]: EVENT_ACTION_BLURRED,
      [EVENT_PROPERTY_DESCRIPTOR]: tracking?.descriptor,
      [EVENT_PROPERTY_UI_CONTEXT]: tracking?.ui_context,
    })

    if (onBlur) {
      onBlur(e)
    }
  }

  return {
    onFocus: handleFocus,
    onBlur: handleBlur,
    onChange: handleChange,
    ...restInputProps,
  }
}
